import { useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Printer, Copy, Eye } from "lucide-react";
import { PlotterCutLabel } from "./PlotterCutLabel";
import { LaminationLabel } from "./LaminationLabel";
import { FinalTraceabilityLabel } from "./FinalTraceabilityLabel";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import type { LabelType, LabelSize } from "@/types/labels";

interface PrintLabelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  labelType: LabelType;
  entityId: string;
  entityName?: string;
  plotterCut?: any;
  plybookExecution?: any;
  onPrintComplete?: () => void;
}

export function PrintLabelDialog({
  open,
  onOpenChange,
  labelType,
  entityId,
  entityName,
  plotterCut,
  plybookExecution,
  onPrintComplete,
}: PrintLabelDialogProps) {
   
  const { user } = useAuth();
  const [copies, setCopies] = useState(1);
  const [size, setSize] = useState<LabelSize>("medium");
  const [shortCode, setShortCode] = useState<string | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Etiqueta-${labelType}-${entityId}`,
    onBeforePrint: async () => {
      setIsPrinting(true);
      try {
        // Generate short code if not exists
        let code = shortCode;
        if (!code) {
          const { data, error } = await api.rpc("generate_label_short_code", {
            p_type: labelType,
          });
          if (error) throw error;
          code = data;
          setShortCode(code);
        }

        // Build data snapshot
        const dataSnapshot: Record<string, unknown> = {};
        if (labelType === "plotter" && plotterCut) {
          dataSnapshot.package_code = plotterCut.package_code;
          dataSnapshot.package_name = plotterCut.package_name;
          dataSnapshot.batch_number = plotterCut.batch_number;
        } else if (labelType === "lamination" && plybookExecution) {
          dataSnapshot.execution_id = plybookExecution.id;
          dataSnapshot.plybook_code = plybookExecution.plybook?.code;
        }

        // Record print
        await api.from("label_prints").insert({
          short_code: code,
          label_type: labelType,
          plotter_cut_id: labelType === "plotter" ? entityId : null,
          plybook_execution_id: labelType === "lamination" ? entityId : null,
          work_order_id: labelType === "final" ? entityId : plybookExecution?.work_order_id,
          data_snapshot: dataSnapshot,
          printed_by: user?.id,
          copies_printed: copies,
        });

        return Promise.resolve();
      } catch (err) {
        console.error("Error recording print:", err);
        toast.error("Error al registrar la impresion");
        return Promise.reject(err);
      }
    },
    onAfterPrint: () => {
      setIsPrinting(false);
      toast.success(`Etiqueta impresa (${copies} copia${copies > 1 ? "s" : ""})`);
      onPrintComplete?.();
    },
    onPrintError: (err) => {
      setIsPrinting(false);
      console.error("Print error:", err);
      toast.error("Error al imprimir");
    },
  });

  const labelTitles: Record<LabelType, string> = {
    plotter: "Etiqueta Corte Plotter",
    lamination: "Etiqueta Post-Laminado",
    final: "Etiqueta Trazabilidad Final",
  };

  const renderLabel = () => {
    switch (labelType) {
      case "plotter":
        return (
          <PlotterCutLabel
            ref={printRef}
            plotterCut={plotterCut}
            plotterCutId={entityId}
            shortCode={shortCode || undefined}
            size={size}
          />
        );
      case "lamination":
        return (
          <LaminationLabel
            ref={printRef}
            plybookExecution={plybookExecution}
            plybookExecutionId={entityId}
            shortCode={shortCode || undefined}
            size={size}
          />
        );
      case "final":
        return (
          <FinalTraceabilityLabel
            ref={printRef}
            workOrderId={entityId}
            shortCode={shortCode || undefined}
            size={size}
          />
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="w-5 h-5" />
            {labelTitles[labelType]}
          </DialogTitle>
          {entityName && (
            <DialogDescription>{entityName}</DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4">
          {/* Options */}
          <div className="flex gap-4">
            <div className="flex-1">
              <Label htmlFor="copies">Copias</Label>
              <div className="flex items-center gap-2 mt-1">
                <Copy className="w-4 h-4 text-muted-foreground" />
                <Input
                  id="copies"
                  type="number"
                  min={1}
                  max={10}
                  value={copies}
                  onChange={(e) => setCopies(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                  className="w-20"
                />
              </div>
            </div>

            <div className="flex-1">
              <Label htmlFor="size">Tamano</Label>
              <Select value={size} onValueChange={(v) => setSize(v as LabelSize)}>
                <SelectTrigger id="size" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">Pequena (50x25mm)</SelectItem>
                  <SelectItem value="medium">Mediana (100x50mm)</SelectItem>
                  <SelectItem value="large">Grande (150x100mm)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Preview */}
          <div className="border rounded-lg p-4 bg-muted/30">
            <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
              <Eye className="w-4 h-4" />
              Vista previa
            </div>
            <div className="flex justify-center overflow-auto max-h-[400px] bg-white rounded p-2">
              {renderLabel()}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={() => handlePrint()} disabled={isPrinting}>
            <Printer className="w-4 h-4 mr-2" />
            {isPrinting ? "Imprimiendo..." : `Imprimir ${copies > 1 ? `(${copies})` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
